import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'compact' | 'featured';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="flex items-start gap-3 py-3 group border-b border-background-200/70 last:border-b-0 cursor-pointer"
      >
        <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
          <i className="ri-file-text-line text-sm"></i>
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
            {article.title}
          </h4>
          <div className="flex items-center gap-3 mt-1 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.views >= 1000 ? `${(article.views / 1000).toFixed(1)}k` : article.views}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-1-line"></i>
              {article.comments}
            </span>
            <span>{article.publishDate}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="block p-5 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300/60 transition-all duration-300 group cursor-pointer"
      >
        <div className="flex items-center gap-2 mb-3">
          {article.isPinned && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-700">
              <i className="ri-pushpin-line text-xs"></i>置顶
            </span>
          )}
          {article.isHot && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
              <i className="ri-fire-line text-xs"></i>热门
            </span>
          )}
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700">
            {article.categoryName}
          </span>
        </div>
        <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors mb-2 line-clamp-2 font-heading">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 line-clamp-2 mb-3 leading-relaxed">
          {article.excerpt}
        </p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img
              src={article.avatar}
              alt={article.author}
              className="w-6 h-6 rounded-full object-cover"
              width="24"
              height="24"
            />
            <span className="text-xs text-foreground-500">{article.author}</span>
          </div>
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.views >= 1000 ? `${(article.views / 1000).toFixed(1)}k` : article.views}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-1-line"></i>
              {article.comments}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-heart-line"></i>
              {article.likes}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="flex flex-col sm:flex-row gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300/60 transition-all duration-300 group cursor-pointer"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700">
            {article.categoryName}
          </span>
          {article.isHot && (
            <span className="inline-flex items-center gap-0.5 text-xs text-accent-600">
              <i className="ri-fire-line"></i>热帖
            </span>
          )}
        </div>
        <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors mb-1.5 line-clamp-2 font-heading">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 line-clamp-2 mb-3 leading-relaxed">
          {article.excerpt}
        </p>
        <div className="flex items-center gap-3 text-xs text-foreground-500 flex-wrap">
          <div className="flex items-center gap-1.5">
            <img
              src={article.avatar}
              alt={article.author}
              className="w-5 h-5 rounded-full object-cover"
              width="20"
              height="20"
            />
            <span>{article.author}</span>
          </div>
          <span>{article.publishDate}</span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {article.views >= 1000 ? `${(article.views / 1000).toFixed(1)}k` : article.views}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-chat-1-line"></i>
            {article.comments}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-heart-line"></i>
            {article.likes}
          </span>
        </div>
      </div>
    </Link>
  );
}